import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from '@/contexts/LanguageContext';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { CalendarIcon, Copy, Users, Building2 } from 'lucide-react';

interface CopyConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  shiftsCount: number;
  withGuards: boolean;
  copyMode: 'week' | 'custom' | 'month';
  sourceDate: Date;
  teamName?: string;
}

const CopyConfirmDialog: React.FC<CopyConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  shiftsCount,
  withGuards,
  copyMode,
  sourceDate,
  teamName
}) => {
  const { t } = useLanguage();

  const formatDateRange = () => {
    if (copyMode === 'week') {
      const weekStart = startOfWeek(sourceDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(sourceDate, { weekStartsOn: 1 });
      return `${format(weekStart, 'd MMM')} to ${format(weekEnd, 'd MMM yyyy')}`;
    } else if (copyMode === 'month') {
      return format(sourceDate, 'MMMM yyyy');
    } else {
      return format(sourceDate, 'MMM d, yyyy');
    }
  };

  const getModeText = () => {
    switch (copyMode) {
      case 'week':
        return t("week") || "week";
      case 'month':
        return t("month") || "month";
      case 'custom':
        return t("selectedRange") || "selected range";
      default:
        return "";
    }
  };

  const handleConfirm = () => {
    console.log('CopyConfirmDialog: handleConfirm START');
    console.log('CopyConfirmDialog: onConfirm type:', typeof onConfirm);
    console.log('CopyConfirmDialog: onClose type:', typeof onClose);
    
    try {
      console.log('CopyConfirmDialog: calling onConfirm...');
      onConfirm();
      console.log('CopyConfirmDialog: onConfirm completed successfully');
    } catch (error) {
      console.error('CopyConfirmDialog: Error in confirm callback:', error);
    }
    
    console.log('CopyConfirmDialog: calling onClose...');
    onClose();
    console.log('CopyConfirmDialog: handleConfirm END');
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            {t("confirmCopy") || "Confirm Copy"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {t("copyFrom") || "Copy from"}: {formatDateRange()} ({getModeText()})
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {shiftsCount} {shiftsCount === 1 ? (t("shift") || "shift") : (t("shifts") || "shifts")}
                  </p>
                </div>
              </div>

              {teamName && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {t("team") || "Team"}: {teamName}
                  </span>
                </div>
              )}

              {withGuards && (
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-600">
                    {t("includeAssignedGuards") || "Include assigned guards"}
                  </span>
                </div>
              )}

              <p className="text-sm">
                {t("confirmCopyMessage") || `Are you sure you want to copy ${shiftsCount} shifts${withGuards ? " with assigned guards" : ""}?`}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => {
            console.log('CopyConfirmDialog: Cancel button clicked');
            console.log('CopyConfirmDialog: onClose type:', typeof onClose);
            onClose();
            console.log('CopyConfirmDialog: Cancel onClose completed');
          }}>
            {t("cancel") || "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            {t("copyShifts") || "Copy Shifts"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CopyConfirmDialog;